import { invalidateDashboardAfterWrite } from '@/api/dashboard/invalidate-dashboard';
import { invalidateCommesseAfterFinancialWrite } from '@/api/commessa/invalidate-commesse';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import qs from 'query-string';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { ErrorData } from '@/api/models';
import { shouldReportServerError } from '@/api/common/server-error-reporting';
import { ERROR_KEYS, isValidDateString, parseDateString } from '@repo/utils';
import Cookies from 'js-cookie';
import { CSRF_TOKEN } from '@/lib/constants/tokens';
import { removeTokenCookies } from '@/lib/auth/utils';
import { buildSignInUrl } from '@/lib/auth/post-login-redirect';
import { SIGN_IN } from '@/lib/constants/routes';
import { dispatchTenantInactiveEvent } from '@/lib/events/tenant-inactive-event';
import {
  dispatchCommercialLimitEvent,
  resolveCommercialLimitEventDetail,
} from '@/lib/events/commercial-limit-event';
import {
  dispatchPermissionPreviewEvent,
  resolvePermissionPreviewEventDetail,
} from '@/lib/events/permission-preview-event';
import { buildPermissionPreviewRequestHeaders } from '@/lib/permission-preview/client-preview-header';
import { runtimeConfig } from '@/lib/runtime-config';

// Regex for ISO datetime strings (not covered by @repo/utils)
const ISO_DATETIME_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?$/;

/**
 * Recursively converts date strings in API responses to Date objects.
 * - YYYY-MM-DD strings are converted to UTC midnight Date objects (via parseDateString)
 * - ISO datetime strings are converted to Date objects
 */
function convertDates<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    // Handle date-only strings (YYYY-MM-DD) using existing utility
    if (isValidDateString(value)) {
      return parseDateString(value) as T;
    }
    // Handle ISO datetime strings
    if (ISO_DATETIME_REGEX.test(value)) {
      return new Date(value) as T;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(convertDates) as T;
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      result[key] = convertDates((value as Record<string, unknown>)[key]);
    }
    return result as T;
  }

  return value;
}

// Extend Axios types to support skipAuth flag
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

const BASE_URL = runtimeConfig.VITE_BASE_URL;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 1,
    },
  },
});

// Promise-based lock to prevent multiple simultaneous redirects
let redirectPromise: Promise<void | null> | null = null;

// Helper function to handle authentication failure
const handleAuthFailure = async (): Promise<void | null> => {
  // If already redirecting, wait for that redirect to complete
  if (redirectPromise) {
    return redirectPromise;
  }

  redirectPromise = (async () => {
    try {
      removeTokenCookies();

      if (!window.location.pathname.includes(SIGN_IN)) {
        // Anche il 401 conserva la posizione: chi arriva da un deep link di
        // una email con la sessione scaduta ci torna sopra dopo il login.
        window.location.replace(
          buildSignInUrl({
            pathname: window.location.pathname,
            searchStr: window.location.search,
          })
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } finally {
      redirectPromise = null;
    }
  })();

  return redirectPromise;
};

// Extend Axios config to support skipAuth flag
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
}

export const client = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  paramsSerializer: function (params) {
    return qs.stringify(params, { encode: true, arrayFormat: 'comma' });
  },
});

client.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    const method = (config.method || 'GET').toUpperCase();
    const needsCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method);
    const csrfToken = Cookies.get(CSRF_TOKEN);

    if (!config.skipAuth && needsCsrf && csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    // Anche sulle GET: una tab rimasta indietro deve uscire dall'anteprima
    // alla prima richiesta, non solo alla prima scrittura.
    for (const [name, value] of Object.entries(
      buildPermissionPreviewRequestHeaders(queryClient)
    )) {
      config.headers[name] = value;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for centralized error handling and date conversion
client.interceptors.response.use(
  (response: AxiosResponse) => {
    void invalidateDashboardAfterWrite(queryClient, response.config);
    void invalidateCommesseAfterFinancialWrite(queryClient, response.config);
    // Skip date conversion for binary responses (Blob, ArrayBuffer)
    if (
      response.config.responseType === 'blob' ||
      response.config.responseType === 'arraybuffer'
    ) {
      return response;
    }
    // Convert date strings to Date objects in response data
    response.data = convertDates(response.data);
    return response;
  },
  async (error) => {
    const originalRequest = error.config as
      | CustomAxiosRequestConfig
      | undefined;

    // When responseType is 'blob' and server returns an error, parse JSON from Blob
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        error.response.data = JSON.parse(text);
      } catch {
        // If not valid JSON, leave the Blob as-is
      }
    }

    if (
      error?.response?.data?.message === ERROR_KEYS.TENANTS.TENANT_NOT_ACTIVE &&
      !originalRequest?.skipAuth
    ) {
      dispatchTenantInactiveEvent();
      return Promise.reject(error);
    }

    const commercialLimit = resolveCommercialLimitEventDetail(
      error?.response?.data ?? {}
    );
    if (commercialLimit) {
      dispatchCommercialLimitEvent(commercialLimit);
    }

    // Solo il segnale: il toast della mutation resta quello standard, con la
    // chiave tradotta dell'errore.
    const permissionPreviewEvent = resolvePermissionPreviewEventDetail(
      error?.response?.data
    );
    if (permissionPreviewEvent) {
      dispatchPermissionPreviewEvent(permissionPreviewEvent);
    }

    if (
      error.response?.status === 403 &&
      error.response.data?.message === ERROR_KEYS.TENANTS.TENANT_NOT_ACTIVE &&
      !originalRequest?.skipAuth
    ) {
      dispatchTenantInactiveEvent();
      return Promise.reject(error);
    } else if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.skipAuth
    ) {
      await handleAuthFailure();
    }

    // Capture server errors (5xx) to Sentry, except a temporarily unavailable DB
    if (shouldReportServerError(error.response)) {
      Sentry.captureException(error, {
        tags: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
        },
        extra: {
          statusCode: error.response.status,
          responseData: error.response.data,
        },
      });
    }

    // Handle HTTP errors (4xx, 5xx)
    if (error.response) {
      throw new ErrorData(error.response.data);
    }
    // Network error or request setup error - pass through
    throw error;
  }
);
